import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Select,
  MenuItem,
  InputBase,
  useTheme,
  useMediaQuery,
  Card,
  Toolbar,
  InputAdornment,
  TextField,
  TablePagination,
  Menu,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import { useNavigate } from 'react-router-dom';
import {
  createProposal,
  publishProposal,
  unpublishProposal,
  deleteProposal,
  updateProposalStatus,
  cloneProposal
} from '../../../Services/FirebaseUtils';

const Proposals = ({ leadId, showToolbar = true, showPagination = true, maxHeight }) => {
  const dispatch = useDispatch();
  const { items: allProposals, loading } = useSelector((state) => state.proposals);
  const userData = useSelector((state) => state.user.data);
  const navigate = useNavigate();

  const proposals = useMemo(() => {
    if (leadId) {
      return allProposals.filter(proposal => proposal.leadId === leadId);
    }
    return allProposals;
  }, [allProposals, leadId]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('All');
  const [cityFilter, setCityFilter] = useState('All');
  const [selectedProposalId, setSelectedProposalId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [publicUrlDialog, setPublicUrlDialog] = useState({ open: false, url: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const theme = useTheme();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleMenuClick = (event, proposalId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedProposalId(proposalId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = (proposalId) => {
    navigate(`/private/dashboard/proposals/edit/${proposalId}`, { state: { mode: 'edit' } });
    handleMenuClose();
  };

  const handleViewClick = (proposalId) => {
    navigate(`/private/dashboard/proposals/edit/${proposalId}`, { state: { mode: 'view' } });
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft':
        return {
          color: theme.palette.grey[600],
          backgroundColor: theme.palette.grey[100],
        };
      case 'Approved':
        return {
          color: theme.palette.warning.dark,
          backgroundColor: theme.palette.warning.light,
        };
      case 'Published':
        return {
          color: theme.palette.success.dark,
          backgroundColor: theme.palette.success.light,
        };
      default:
        return {
          color: theme.palette.grey[600],
          backgroundColor: theme.palette.grey[100],
        };
    }
  };

  const getShortlistCount = (proposal) => {
    if (!proposal.shortlist) return 0;
    return proposal.shortlist.length;
  };
  const getQuestionsCount = (proposal) => {
    if (!proposal.feedback) return '0';
    const feedbackArray = Object.values(proposal.feedback);
    let answeredQuestionsCount = 0;
    let questionCount = 0;
    feedbackArray.map((feedback) => {
      if (!feedback.feedbackItem) return;
      const feedbackItemArray = Object.values(feedback.feedbackItem);
      feedbackItemArray.map((item) => {
        questionCount += 1;
        if (item.answer && item.answer !== '') {
          answeredQuestionsCount += 1;
        }
      });
      return null;
    });
    return `${answeredQuestionsCount}/${questionCount}`;
  };

  const getNotesCount = (proposal) => {
    if (!proposal.notes) return 0;
    return Object.keys(proposal.notes).length;
  };

  const getViewersCount = (proposal) => {
    return proposal.viewers? Object.keys(proposal.viewers).length : 0;
  };

  const getViewersList = (proposal) => {
    if (!proposal.viewers || proposal.viewers.length === 0) {
      return "No viewers yet";
    }
    let viewersList = Object.values(proposal.viewers);
    console.log("viewersList", viewersList);
    return viewersList.join('\n');
  };

  const getOptionsCount = (proposal) => {
    if (!proposal.proposedOptions) return 0;
    let count = Object.keys(proposal.proposedOptions).length ;
    return count;
  };

  const handleCreateNewProposal = async () => {
    const result = await createProposal(userData.email);
    if (result.success) {
      navigate(`/private/dashboard/proposals/edit/${result.proposalId}`, { state: { mode: 'edit' } });
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await deleteProposal(selectedProposalId);
      if (result.success) {
        setDeleteDialogOpen(false);
      } else {
        console.error('Failed to delete proposal:', result.error);
      }
    } catch (error) {
      console.error('Error deleting proposal:', error);
    }
  };

  const handlePublishClick = async () => {
    try {
      const result = await publishProposal(selectedProposalId);
      if (result.success) {
        setPublicUrlDialog({ open: true, url: result.publicUrl });
      } else {
        console.error('Failed to publish proposal:', result.error);
        setSnackbar({
          open: true,
          message: 'Failed to publish proposal',
          severity: 'error'
        });
      }
      handleMenuClose();
    } catch (error) {
      console.error('Error publishing proposal:', error);
      setSnackbar({
        open: true,
        message: 'Error publishing proposal',
        severity: 'error'
      });
    }
  };

  const handleUnpublishClick = async () => {
    try {
      const result = await unpublishProposal(selectedProposalId);
      if (!result.success) {
        console.error('Failed to unpublish proposal:', result.error);
      }
      handleMenuClose();
    } catch (error) {
      console.error('Error unpublishing proposal:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleCopyUrl = (event, url = null) => {
    if (event) {
      event.stopPropagation();
    }
    const urlToCopy = url || publicUrlDialog.url;
    navigator.clipboard.writeText(urlToCopy).then(() => {
      setSnackbar({
        open: true,
        message: 'URL copied to clipboard',
        severity: 'success'
      });
    }).catch(() => {
      setSnackbar({
        open: true,
        message: 'Failed to copy URL',
        severity: 'error'
      });
    });
  };

  const handleClosePublicUrl = () => {
    setPublicUrlDialog({ open: false, url: '' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getPublicUrl = (proposalId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/public/published-proposal/login?proposalId=${proposalId}`;
  };

  const getPublicNonLoginUrl = (proposalId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/public/published-proposal?proposalId=${proposalId}`;
  };

  const handleCopyPublicUrl = (event, proposalId, isNonLogin = false) => {
    event.stopPropagation();
    const url = isNonLogin ? getPublicNonLoginUrl(proposalId) : getPublicUrl(proposalId);
    navigator.clipboard.writeText(url).then(() => {
      setSnackbar({
        open: true,
        message: 'URL copied to clipboard',
        severity: 'success'
      });
    }).catch(() => {
      setSnackbar({
        open: true,
        message: 'Failed to copy URL',
        severity: 'error'
      });
    });
  };

  const handleClone = async () => {
    try {
      const result = await cloneProposal(selectedProposalId);
      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: 'success'
        });
        handleMenuClose();
      } else {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error cloning proposal:', error);
      setSnackbar({
        open: true,
        message: 'Error cloning proposal',
        severity: 'error'
      });
    }
  };

  // Get unique owners and cities
  const uniqueOwners = ['All', ...new Set(proposals.map(proposal => proposal.owner || '').filter(Boolean))];
  const uniqueCities = ['All', ...new Set(proposals.map(proposal => proposal.city || ''))];

  const filteredProposals = proposals.filter(proposal => {
    const matchesSearch = proposal.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      proposal.proposalId?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesOwner = ownerFilter === 'All' || proposal.owner === ownerFilter;
    const matchesCity = cityFilter === 'All' || proposal.city === cityFilter;

    return matchesSearch && matchesOwner && matchesCity;
  });

  const PROPOSAL_STAGES = [
    'Draft',
    'Published',
    'Shortlisting',
    'Visits',
    'Negotiation',
    'Closed'
  ];

  const StatusStepper = ({ status }) => {
    const theme = useTheme();
    const steps = ['Draft', 'Published', 'Shortlisting', 'Visits', 'Negotiation', 'Closed'];
    const currentStep = steps.findIndex(step => {
      if (step === 'Closed') {
        return status === 'Closed Won' || status === 'Closed Lost';
      }
      return step === status;
    });

    const getStepColor = (index) => {
      if (status === 'Closed Lost' && index === currentStep) {
        return theme.palette.error.main;
      }
      if (index < currentStep) return theme.palette.success.main;
      if (index === currentStep) return theme.palette.primary.main;
      return theme.palette.grey[300];
    };

    const getStepTooltip = (step) => {
      if (step === 'Closed') {
        return status === 'Closed Lost' ? 'Closed Lost' : 'Closed Won';
      }
      return step;
    };

    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.5 }}>
        {steps.map((label, index) => (
          <React.Fragment key={label}>
            <Tooltip title={getStepTooltip(label)} arrow placement="top">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: getStepColor(index),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                {index <= currentStep && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: '#fff'
                    }}
                  />
                )}
              </Box>
            </Tooltip>
            {index < steps.length - 1 && (
              <Box
                sx={{
                  width: 6,
                  height: 1,
                  backgroundColor: index < currentStep 
                    ? theme.palette.success.main 
                    : theme.palette.grey[300]
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Stack>
    );
  };

  const handleStatusChange = async () => {
    try {
      const result = await updateProposalStatus(selectedProposalId, newStatus);
      if (!result.success) {
        throw new Error(result.error);
      }

      setOpenStatusDialog(false);
      setNewStatus('');
      handleMenuClose();

      setSnackbar({
        open: true,
        message: 'Status updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: 'Error updating status: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleStatusDialogOpen = () => {
    const selectedProposal = proposals.find(p => p.id === selectedProposalId);
    setNewStatus(selectedProposal?.status || 'Draft');
    setOpenStatusDialog(true);
  };

  return (
    <Card sx={{ borderRadius: 1 }}>
      <Box sx={{ px: 2, py: 1 }}>
        {showToolbar && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            minHeight: '48px'
          }}>
            {/* Search */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Owner</InputLabel>
                <Select
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  label="Owner"
                >
                  {uniqueOwners.map((owner) => (
                    <MenuItem key={owner} value={owner}>{owner}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>City</InputLabel>
                <Select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  label="City"
                >
                  {uniqueCities.map((city) => (
                    <MenuItem key={city} value={city}>{city}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search proposals..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
            </Box>

            {/* Right side: Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: '20px' }} />}
                onClick={handleCreateNewProposal}
                sx={{
                  textTransform: 'none',
                  px: 2,
                  py: 0.75,
                  height: '36px',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  }
                }}
              >
                New Proposal
              </Button>
            </Box>
          </Box>
        )}

        {/* Separator Line */}
        <Box sx={{
          height: '1px',
          backgroundColor: '#e0e0e0',
          width: '100%',
          mx: -2,
          width: 'calc(100% + 32px)',
          mb: 2
        }} />

        {/* Table */}
        {filteredProposals.length === 0 && !loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5, minHeight: maxHeight ? 'auto' : 200 }}>
            <Typography variant="body1" color="textSecondary">
              No proposals found.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: maxHeight ? maxHeight : 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: (theme) => `${theme.palette.primary.main}08` }}>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: '#6B7280',
                    borderBottom: '1px solid #E5E7EB',
                    py: 1.5
                  }}>
                    Client Name
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: '#6B7280',
                    borderBottom: '1px solid #E5E7EB',
                    py: 1.5
                  }}>
                    City
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: '#6B7280',
                    borderBottom: '1px solid #E5E7EB',
                    py: 1.5
                  }}>
                    Options
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: '#6B7280',
                    borderBottom: '1px solid #E5E7EB',
                    py: 1.5
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: '#6B7280',
                    borderBottom: '1px solid #E5E7EB',
                    py: 1.5
                  }}>
                    Engagement
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: '#6B7280',
                    borderBottom: '1px solid #E5E7EB',
                    py: 1.5
                  }}>
                    Created Date
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: '#6B7280',
                    borderBottom: '1px solid #E5E7EB',
                    py: 1.5
                  }}>
                    Last Modified
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 500,
                    color: '#6B7280',
                    borderBottom: '1px solid #E5E7EB',
                    py: 1.5
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProposals
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((proposal) => (
                    <TableRow
                      key={proposal.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: '#F5F3FF',
                          cursor: 'pointer'
                        },
                        '& .MuiTableCell-root': {
                          py: 1.5
                        }
                      }}
                    >
                      <TableCell sx={{
                        borderBottom: '1px solid #E5E7EB',
                        color: '#111827',
                        py: 1.5
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {proposal.clientName}
                          </Typography>
                          {proposal.status === 'Published' && (
                            <React.Fragment>
                              <Tooltip title="Copy login URL">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleCopyPublicUrl(e, proposal.id)}
                                  sx={{
                                    ml: 1,
                                    '&:hover': {
                                      backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                    }
                                  }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy public URL (no login)">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleCopyPublicUrl(e, proposal.id, true)}
                                  sx={{
                                    ml: 1,
                                    '&:hover': {
                                      backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                    }
                                  }}
                                >
                                  <ShareIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </React.Fragment>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        borderBottom: '1px solid #E5E7EB',
                        color: '#111827',
                        py: 1.5
                      }}>
                        {proposal.city || 'N/A'}
                      </TableCell>
                      <TableCell sx={{
                        borderBottom: '1px solid #E5E7EB',
                        color: '#111827',
                        py: 1.5
                      }}>
                        <Typography variant="body1">
                          {getOptionsCount(proposal)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{
                        borderBottom: '1px solid #E5E7EB',
                        color: '#111827',
                        py: 1.5
                      }}>
                        <Box>
                          <StatusStepper status={proposal.status} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        borderBottom: '1px solid #E5E7EB',
                        color: '#111827',
                        py: 1.5
                      }}>
                        <Box>
                          <Tooltip 
                            title={getViewersList(proposal)} 
                            placement="top-start"
                            sx={{ 
                              whiteSpace: 'pre-line',
                              '& .MuiTooltip-tooltip': {
                                backgroundColor: 'white',
                                color: 'black',
                                boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                                padding: '8px',
                                fontSize: '14px'
                              }
                            }}
                          >
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                              <Box component="span" fontWeight="medium">Viewers:</Box> {getViewersCount(proposal)}
                            </Typography>
                          </Tooltip>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box component="span" fontWeight="medium">Shortlisted:</Box> {getShortlistCount(proposal)}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box component="span" fontWeight="medium">Questions:</Box> {getQuestionsCount(proposal)}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box component="span" fontWeight="medium">Notes :</Box> {getNotesCount(proposal)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        borderBottom: '1px solid #E5E7EB',
                        color: '#111827',
                        py: 1.5
                      }}>
                        {proposal.createdAt ? new Date(proposal.createdAt.split(' at ')[0]).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }) : ''}
                      </TableCell>
                      <TableCell sx={{
                        borderBottom: '1px solid #E5E7EB',
                        color: '#111827',
                        py: 1.5
                      }}>
                        {proposal.lastModified ? new Date(proposal.lastModified.split(' at ')[0]).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }) : ''}
                      </TableCell>
                      <TableCell sx={{
                        borderBottom: '1px solid #E5E7EB',
                        color: '#111827',
                        py: 1.5
                      }}>
                        <IconButton
                          size="small"
                          onClick={(event) => handleMenuClick(event, proposal.id)}
                        >
                          <MoreVertIcon sx={{ fontSize: '1.2rem', color: '#6B7280' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {showPagination && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <TablePagination
            component="div"
            count={filteredProposals.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              '.MuiTablePagination-toolbar': {
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              },
              '.MuiTablePagination-displayedRows': {
                margin: 0,
              },
              '.MuiTablePagination-selectLabel': {
                margin: 0,
              },
              '.MuiTablePagination-select': {
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                marginLeft: 1,
                marginRight: 2,
                paddingTop: 0.5,
                paddingBottom: 0.5,
              },
              '.MuiTablePagination-actions': {
                marginLeft: 2,
                display: 'flex',
                gap: 1,
              }
            }}
          />
        </Box>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
              minWidth: '120px'
            }
          }}
        >
          <MenuItem onClick={() => handleViewClick(selectedProposalId)}>View</MenuItem>
          <MenuItem onClick={() => handleEditClick(selectedProposalId)}>Edit Proposal</MenuItem>
          <MenuItem onClick={handleClone}>Clone</MenuItem>
          <MenuItem onClick={handleStatusDialogOpen}>Change Status</MenuItem>
          {proposals.find(p => p.id === selectedProposalId)?.status === 'Published' ? (
            <MenuItem onClick={handleUnpublishClick}>Unpublish</MenuItem>
          ) : (
            (() => {
              const selectedProposal = proposals.find(p => p.id === selectedProposalId);
              return selectedProposal && getOptionsCount(selectedProposal) > 0 ? (
                <MenuItem onClick={handlePublishClick}>Publish</MenuItem>
              ) : null;
            })()
          )}
          <MenuItem 
            onClick={handleDeleteClick} 
            sx={{ color: 'dark' }}
            disabled={proposals.find(p => p.id === selectedProposalId)?.status === 'Published'}
          >
            {proposals.find(p => p.id === selectedProposalId)?.status === 'Published' ? 'Cannot Delete Published Proposal' : 'Delete'}
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
        >
          <DialogTitle id="delete-dialog-title">Delete Proposal</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this proposal ?
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Public URL Dialog */}
        <Dialog
          open={publicUrlDialog.open}
          onClose={handleClosePublicUrl}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Proposal Published Successfully</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                value={publicUrlDialog.url}
                InputProps={{
                  readOnly: true,
                  sx: { pr: 1 }
                }}
              />
              <IconButton
                onClick={(e) => handleCopyUrl(e)}
                color="primary"
                sx={{
                  backgroundColor: (theme) => `${theme.palette.primary.main}08`,
                  '&:hover': {
                    backgroundColor: (theme) => `${theme.palette.primary.main}12`,
                  }
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePublicUrl}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Change Status Dialog */}
        <Dialog
          open={openStatusDialog}
          onClose={() => setOpenStatusDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Change Status</DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                id="status-select"
                value={newStatus}
                label="Status"
                onChange={(e) => setNewStatus(e.target.value)}
              >
                {PROPOSAL_STAGES.map((stage) => (
                  <MenuItem key={stage} value={stage}>{stage}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleStatusChange}
              variant="contained"
              disabled={!newStatus || newStatus === proposals.find(p => p.id === selectedProposalId)?.status}
            >
              Update Status
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Card>
  );
};

export default Proposals;